import { inject, Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { environment } from '../../environments/environment'
import { Observable } from 'rxjs'
import { EquipmentDeliveryMethod } from './equipment-ad.service'

/* ── Marketplace M5: SecureTransaction (escrow) — hand-written bridge client ── */

export type SecureTxStatus =
	| 'AWAITING_PAYMENT'
	| 'PAYMENT_FAILED'
	| 'PAYMENT_HELD'
	| 'AWAITING_SHIPMENT'
	| 'SHIPPED'
	| 'DELIVERED'
	| 'BUYER_CONFIRMED'
	| 'RELEASE_APPROVED'
	| 'SETTLEMENT_PENDING'
	| 'SETTLED'
	| 'CANCELLED'
	| 'DISPUTED'

export type SecureTxSide = 'BUYER' | 'SELLER'

/** Who carries the escrow fee — TODO(backend): enum members beyond BUYER/SELLER not confirmed. */
export type SecureTxFeePayer = 'BUYER' | 'SELLER' | 'SPLIT' | string

/** dispute response actions (seller side) — contract ch.26. */
export type SecureDisputeResponseAction =
	| 'ACCEPT_CLAIM'
	| 'ACCEPT_RETURN'
	| 'PROPOSE_SOLUTION'
	| 'PARTIAL_REFUND'
	| 'REJECT'

export interface SecureTxAddress {
	receiverName?: string | null
	phone?: string | null
	fullAddress?: string | null
	cityId?: string | null
	/** Presentation-only (backend may embed in the address snapshot). */
	cityName?: string | null
}

/**
 * Item snapshot frozen at creation (PRD فصل ۱۸). The backend serializes the ad
 * at tx time — every field optional; unknown keys may appear (rendered loosely
 * by the details page as extra key/value rows when present).
 */
export interface SecureTxSnapshot {
	title?: string | null
	mediaUrl?: string | null
	brand?: string | null
	model?: string | null
	condition?: string | null
	conditionFlawDesc?: string | null
	accessories?: string | null
	price?: number | null
	shippingAmount?: number | null
	acceptsReturn?: boolean | null
	returnWindowDays?: number | null
	authenticity?: string | null
	[key: string]: unknown
}

export interface SecureTxShipment {
	method?: EquipmentDeliveryMethod | string | null
	trackingNumber?: string | null
	shippedAt?: string | null
	deliveredAt?: string | null
	/** TODO(backend): carrier name field not confirmed in the contract. */
	carrier?: string | null
}

/** TODO(backend): extension row shape — fields guessed from the request payload. */
export interface SecureTxExtension {
	id?: string
	requestedHours?: number | null
	note?: string | null
	status?: 'PENDING' | 'ACCEPTED' | 'REJECTED' | string | null
	requestedBy?: SecureTxSide | string | null
	createdAt?: string | null
}

export interface SecureTxDisputeEvidence {
	id?: string
	kind?: string | null
	fileId?: string | null
	note?: string | null
	createdAt?: string | null
}

export interface SecureTxDispute {
	id?: string
	reason?: string | null
	description?: string | null
	status?: 'OPEN' | 'RESOLVED' | 'REJECTED' | string | null
	createdAt?: string | null
	evidences?: SecureTxDisputeEvidence[] | null
	responseNote?: string | null
	responseAction?: SecureDisputeResponseAction | null
}

/**
 * ResSecureTransaction — the backend M5 ships live, but the generated client
 * doesn't know it yet (generate-api pulls the deployed swagger of the old
 * release train). Same bridge pattern as CourseAdService: switch imports only
 * after the next API regeneration.
 *
 * Every field optional per contract; gateway-link fields carry BOTH candidate
 * Backend ResSecureTransaction exposes `payLink` (create/verify only).
 */
export interface ResSecureTransaction {
	id?: string
	status?: SecureTxStatus
	adId?: string
	/** Backend sends real kc UUIDs — frontend derives myRole from Keycloak sub. */
	buyerKcUserId?: string | null
	sellerKcUserId?: string | null
	/** Gateway link — present only on create/verify responses. */
	payLink?: string | null
	trackId?: string | null
	adTitle?: string | null
	/** Buyer / seller display names — TODO(backend): exact field names unconfirmed. */
	buyerName?: string | null
	sellerName?: string | null
	counterpartyName?: string | null
	/** Which side the caller is on (drives action buttons) — TODO(backend) unconfirmed. */
	myRole?: SecureTxSide | null
	side?: SecureTxSide | null

	itemAmount?: number | null
	shippingAmount?: number | null
	feeAmount?: number | null
	feePayer?: SecureTxFeePayer | null
	totalPayable?: number | null


	trackingMethod?: EquipmentDeliveryMethod | string | null
	/** One-time in-person handover code (buyer side, after POST /in-person/otp). */
	inPersonCode?: string | null

	address?: SecureTxAddress | null
	shipment?: SecureTxShipment | null
	snapshot?: SecureTxSnapshot | null
	dispute?: SecureTxDispute | null
	extension?: SecureTxExtension | null

	/* Lifecycle timestamps (timeline source of truth). */
	createdAt?: string | null
	paidAt?: string | null
	shippedAt?: string | null
	deliveredAt?: string | null
	confirmedAt?: string | null
	releasedAt?: string | null
	settledAt?: string | null
	cancelledAt?: string | null
	disputedAt?: string | null
}

export interface SecureTxPage {
	content: ResSecureTransaction[]
	totalElements: number
}

export interface SaveSecureTxAddressRequest {
	receiverName: string
	phone: string
	fullAddress: string
	cityId: string
}

export interface SecureTxShipRequest {
	method: EquipmentDeliveryMethod | string
	trackingNumber?: string | null
}

export interface SecureTxExtensionRequest {
	requestedHours: number
	note?: string | null
}

export interface SecureTxDisputeRequest {
	reason: string
	description?: string | null
}

export interface SecureTxDisputeEvidenceRequest {
	kind: string
	fileId: string
	note?: string | null
}

export interface SecureTxDisputeResponseRequest {
	action: SecureDisputeResponseAction
	note?: string | null
	amount?: number | null
}

/** روش‌های ارسال رهگیری‌دار — tracking number is mandatory for these on /ship. */
export const TRACKED_SHIPPING_METHODS: readonly string[] = ['POST', 'TIPAX', 'COURIER', 'FREIGHT']

/**
 * Persian labels + badge severities for every SecureTxStatus (M5 contract):
 * قرمز برای DISPUTED/FAILED، سبز برای SETTLED/HELD، کهربایی برای انتظارها.
 */
export const SECURE_TX_STATUS_META: Record<
	SecureTxStatus,
	{ label: string; severity: 'success' | 'info' | 'warn' | 'danger' | 'secondary' }
> = {
	// نگاشت وضعیت‌ها به ۵ حالت کاربرپسند + استثناها (فاز ساده‌سازی پرداخت امن):
	// در انتظار پرداخت / در انتظار ارسال / ارسال شد / تحویل شد / تکمیل شد
	// وضعیت‌های میانی تسویه به‌صورت «در حال پردازش» نمایش داده می‌شوند.
	AWAITING_PAYMENT: { label: 'در انتظار پرداخت', severity: 'warn' },
	PAYMENT_FAILED: { label: 'پرداخت ناموفق', severity: 'danger' },
	PAYMENT_HELD: { label: 'در انتظار ارسال', severity: 'warn' },
	AWAITING_SHIPMENT: { label: 'در انتظار ارسال', severity: 'warn' },
	SHIPPED: { label: 'ارسال شد', severity: 'info' },
	DELIVERED: { label: 'تحویل شد — در انتظار تأیید', severity: 'warn' },
	BUYER_CONFIRMED: { label: 'در حال پردازش', severity: 'info' },
	RELEASE_APPROVED: { label: 'در حال پردازش', severity: 'info' },
	SETTLEMENT_PENDING: { label: 'در حال پردازش', severity: 'info' },
	SETTLED: { label: 'تکمیل شد', severity: 'success' },
	CANCELLED: { label: 'لغو شده', severity: 'secondary' },
	DISPUTED: { label: 'ثبت مشکل — در بررسی', severity: 'danger' },
}

export function secureTxStatusLabel(status?: SecureTxStatus | string | null): string {
	return status ? (SECURE_TX_STATUS_META[status as SecureTxStatus]?.label ?? status) : '—'
}

export function secureTxStatusSeverity(
	status?: SecureTxStatus | string | null,
): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
	return status ? (SECURE_TX_STATUS_META[status as SecureTxStatus]?.severity ?? 'secondary') : 'secondary'
}

/** feePayer → Persian (TODO(backend): SPLIT member unconfirmed). */
export function feePayerLabel(payer?: SecureTxFeePayer | null): string {
	switch (payer) {
		case 'BUYER':
			return 'خریدار'
		case 'SELLER':
			return 'فروشنده'
		case 'SPLIT':
			return 'نصف‌نصف'
		default:
			return (payer as string) ?? '—'
	}
}

/** Gateway link helper — whichever name the backend picked (see field TODOs). */
export function secureTxPayUrl(tx: ResSecureTransaction | null | undefined): string | null {
	return tx?.payLink ?? null
}

/**
 * Manually-written client for the `/secure/*` escrow endpoints (marketplace M5).
 * All routes authenticated (buyer/seller); errors surface via mutation toasts.
 */
@Injectable({ providedIn: 'root' })
export class SecureTransactionService {
	private http = inject(HttpClient)
	private base = environment.apiUrl
	private root = `${this.base}/secure/transactions`

	/* ── Lifecycle ─────────────────────────────────────────────────────── */

	/**
	 * Buyer creates the transaction (escrow draft). `trackingMethod` is the
	 * chosen delivery method; `trackingNumber` stays empty at creation (the
	 * seller supplies it on /ship). Negotiated-price drafts additionally send
	 * proposedPrice/proposedNote — TODO(backend): query names unconfirmed on
	 * ResSecureTransaction; drop once the swagger lands.
	 */
	create(params: {
		adId: string
		shippingAmount?: number | null
		trackingMethod?: string | null
		trackingNumber?: string | null
		proposedPrice?: number | null
		proposedNote?: string | null
	}): Observable<ResSecureTransaction> {
		const query: string[] = [`adId=${encodeURIComponent(params.adId)}`]
		if (params.shippingAmount != null) query.push(`shippingAmount=${params.shippingAmount}`)
		if (params.trackingMethod) query.push(`trackingMethod=${encodeURIComponent(params.trackingMethod)}`)
		if (params.trackingNumber) query.push(`trackingNumber=${encodeURIComponent(params.trackingNumber)}`)
		if (params.proposedPrice != null) query.push(`proposedPrice=${params.proposedPrice}`)
		if (params.proposedNote) query.push(`proposedNote=${encodeURIComponent(params.proposedNote)}`)
		return this.http.post<ResSecureTransaction>(`${this.root}?${query.join('&')}`, {})
	}

	/** Idempotent verify — call once the gateway returns the user. */
	verify(id: string): Observable<ResSecureTransaction> {
		return this.http.get<ResSecureTransaction>(`${this.root}/${id}/verify`)
	}

	getMy(params: {
		side?: SecureTxSide | null
		status?: string | null
		page?: number
		size?: number
	}): Observable<SecureTxPage> {
		const query: string[] = []
		if (params.side) query.push(`side=${params.side}`)
		if (params.status) query.push(`status=${encodeURIComponent(params.status)}`)
		if (params.page != null) query.push(`page=${params.page}`)
		if (params.size != null) query.push(`size=${params.size}`)
		const qs = query.length ? `?${query.join('&')}` : ''
		return this.http.get<SecureTxPage>(`${this.root}/my${qs}`)
	}

	getOne(id: string): Observable<ResSecureTransaction> {
		return this.http.get<ResSecureTransaction>(`${this.root}/${id}`)
	}

	/* ── Buyer ─────────────────────────────────────────────────────────── */

	confirmDelivery(id: string): Observable<ResSecureTransaction> {
		return this.http.post<ResSecureTransaction>(`${this.root}/${id}/confirm-delivery`, {})
	}

	reportProblem(id: string, note: string): Observable<ResSecureTransaction> {
		return this.http.post<ResSecureTransaction>(
			`${this.root}/${id}/report-problem?note=${encodeURIComponent(note)}`,
			{},
		)
	}

	updateAddress(id: string, address: SaveSecureTxAddressRequest): Observable<ResSecureTransaction> {
		return this.http.put<ResSecureTransaction>(`${this.root}/${id}/address`, address)
	}

	/** Buyer requests the one-time handover code for IN_PERSON deals. */
	requestInPersonOtp(id: string): Observable<ResSecureTransaction> {
		return this.http.post<ResSecureTransaction>(`${this.root}/${id}/in-person/otp`, {})
	}

	/* ── Seller ────────────────────────────────────────────────────────── */

	ship(id: string, request: SecureTxShipRequest): Observable<ResSecureTransaction> {
		return this.http.post<ResSecureTransaction>(`${this.root}/${id}/ship`, request)
	}

	requestExtension(id: string, request: SecureTxExtensionRequest): Observable<ResSecureTransaction> {
		return this.http.post<ResSecureTransaction>(`${this.root}/${id}/extension/request`, request)
	}

	/** Seller confirms an IN_PERSON handover with the buyer's code. */
	confirmInPerson(id: string, code: string): Observable<ResSecureTransaction> {
		return this.http.post<ResSecureTransaction>(`${this.root}/${id}/in-person/confirm`, { code })
	}

	/* ── Extension vote (counterparty of the requester) ────────────────── */

	acceptExtension(extensionId: string): Observable<ResSecureTransaction> {
		return this.http.post<ResSecureTransaction>(`${this.base}/secure/extension/${extensionId}/accept`, {})
	}

	rejectExtension(extensionId: string): Observable<ResSecureTransaction> {
		return this.http.post<ResSecureTransaction>(`${this.base}/secure/extension/${extensionId}/reject`, {})
	}

	/* ── Disputes ──────────────────────────────────────────────────────── */

	openDispute(id: string, request: SecureTxDisputeRequest): Observable<ResSecureTransaction> {
		return this.http.post<ResSecureTransaction>(`${this.root}/${id}/dispute`, request)
	}

	addDisputeEvidence(disputeId: string, request: SecureTxDisputeEvidenceRequest): Observable<boolean> {
		return this.http.post<boolean>(`${this.base}/secure/disputes/${disputeId}/evidence`, request)
	}

	respondToDispute(disputeId: string, request: SecureTxDisputeResponseRequest): Observable<boolean> {
		return this.http.post<boolean>(`${this.base}/secure/disputes/${disputeId}/response`, request)
	}
}
